import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { setAiCreditsOrgSource } from '@workspace/ui/hooks/use-ai-credits';
import { getActiveOrgId } from './lib/active-org';
import { installDomTranslationGuard } from './lib/dom-translation-guard';
import { installPreloadErrorRecovery } from './lib/preload-recovery';
import { initSentry } from './lib/sentry/init';
import { initClarity } from './utils/clarity';
import { App } from './App';
import './global.css';

// Guard DOM reconciliation against Google Translate and extension DOM manipulations
installDomTranslationGuard();

// Builder URLs carry no org segment; credits must be scoped to the org of the loaded
// proposal (in-memory holder), not the session's default org;
setAiCreditsOrgSource(() => {
  const orgId = getActiveOrgId();
  return orgId != null ? String(orgId) : null;
});

initSentry();

// Starts recording before auth so guest sessions are captured; AuthLayout's identifyClarity
// tags the session with the user once the session check resolves.
initClarity();

// Stale asset hashes after a redeploy (or a flaky/blocked request) can fail to
// preload a lazy chunk's JS/CSS — reload to pick up current assets.
installPreloadErrorRecovery();

createRoot(document.getElementById('root')!, {
  // Caught render errors go through Page/Block ErrorBoundary → captureReactRenderError.
  // These hooks cover trees with no boundary (guest/view) and recovered Suspense errors.
  onUncaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(<App />);
